import {
  normalizeVaultTenantId,
  persistVaultTenantScopeToStorage,
  resolveVaultActiveTenantIdFromTokenAndStorage,
} from './vault-tenant-scope.util';
import { isSimappeProductCompanyLabel } from './vault-nebula-company.util';

const VAULT_TENANT_SNAPSHOT_KEY = 'vault_tenant_snapshot';

export interface VaultTenantSnapshot {
  tenant_id: string;
  tenant_name: string;
  role: string;
}

export function readVaultTenantSnapshot(): VaultTenantSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(VAULT_TENANT_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultTenantSnapshot;
    if (!parsed || typeof parsed !== 'object') return null;
    const tenant_id = normalizeVaultTenantId(String(parsed.tenant_id ?? ''));
    if (!tenant_id) return null;
    const tenant_name = String(parsed.tenant_name ?? '').trim();
    const role = String(parsed.role ?? '').trim();
    return {
      tenant_id,
      tenant_name: isSimappeProductCompanyLabel(tenant_name) ? '' : tenant_name,
      role,
    };
  } catch {
    return null;
  }
}

/** Persiste empresa Vault asignada (sobrevive refresh JWT y re-sync de sesión). */
export function persistVaultTenantSnapshot(partial: {
  tenant_id?: string;
  tenant_name?: string;
  role?: string;
}): void {
  if (typeof localStorage === 'undefined') return;
  const tenant_id = normalizeVaultTenantId(partial.tenant_id);
  if (!tenant_id) return;

  const prev = readVaultTenantSnapshot();
  const tenant_name = (partial.tenant_name ?? prev?.tenant_name ?? '').trim();
  const role = (partial.role ?? prev?.role ?? '').trim();

  localStorage.setItem(
    VAULT_TENANT_SNAPSHOT_KEY,
    JSON.stringify({
      tenant_id,
      tenant_name: isSimappeProductCompanyLabel(tenant_name) ? prev?.tenant_name ?? '' : tenant_name,
      role,
    } satisfies VaultTenantSnapshot),
  );
}

export function clearVaultTenantSnapshot(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_TENANT_SNAPSHOT_KEY);
}

export function hydrateVaultTenantSnapshotToSession(
  persistTenants: (rows: unknown) => void,
  persistCatalog: (rows: unknown) => void,
): string {
  const snapshot = readVaultTenantSnapshot();
  if (!snapshot?.tenant_id) return '';

  const row = {
    tenant_id: snapshot.tenant_id,
    tenant_name: snapshot.tenant_name,
    role: snapshot.role,
  };
  persistTenants([row]);
  if (snapshot.tenant_name) {
    persistCatalog([{ id: snapshot.tenant_id, name: snapshot.tenant_name }]);
  }
  persistVaultTenantScopeToStorage(snapshot.tenant_id);
  return snapshot.tenant_id;
}

/** Re-aplica snapshot estable y persiste estado derivado tras cada sync de sesión. */
export function reconcileVaultTenantSnapshotFromSession(
  persistTenants: (rows: unknown) => void,
  persistCatalog: (rows: unknown) => void,
): string {
  const snapshot = readVaultTenantSnapshot();
  if (snapshot?.tenant_id) {
    hydrateVaultTenantSnapshotToSession(persistTenants, persistCatalog);
    return snapshot.tenant_id;
  }

  const tenantId = resolveVaultActiveTenantIdFromTokenAndStorage();
  if (!tenantId) return '';

  persistVaultTenantSnapshot({ tenant_id: tenantId });
  persistVaultTenantScopeToStorage(tenantId);
  return tenantId;
}
